import { Button } from 'ui';
import { Dashboard, DashboardMainHeaderForm } from 'dashboard/components/dashboard';
import { useConfirm } from 'ui/contexts/overlay/Confirm';
import { Page } from 'dashboard/components/dashboard/Breadcrumbs';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import useToastContext from 'ui/hooks/useToast';
import { ErrorOption, SubmitHandler, useForm } from 'react-hook-form';
import FinancialAccountFields, {
  FormFields,
  FORM_FIELDS,
} from 'financialAccounts/components/form/FinancialAccountsFormFields';
import { useLoading } from 'ui/contexts/overlay/Loading';
import { ApolloError, useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  FinancialAccountUpdated,
  FinancialAccountUpdateMutationVariables,
  FINANCIAL_ACCOUNT_UPDATE_MUTATION,
} from 'financialAccounts/graphql/financialAccountUpdateMutation';
import { setFormError } from 'utils/form';
import {
  financialAccountTypename,
  FINANCIAL_ACCOUNT_QUERY,
} from 'financialAccounts/graphql/financialAccountQuery';
import { DEFAULT_OVERLAY_CONFIG, ToastProps } from 'ui/contexts/overlay/Toast';
import {
  FinancialAccountDeleteMutationVariables,
  FINANCIAL_ACCOUNT_DELETE_MUTATION,
} from 'financialAccounts/graphql/financialAccountDeleteMutation';
import { OverlayConfig, OverlayProps } from 'ui/models/overlay';

import DeleteButton from 'financialAccounts/components/DeleteButton';

import { has } from 'lodash';
import { updateCacheById } from 'graphql/apollo/cache';

const DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE =
  '/dashboard/billing-cycle/financial-accounts';

const BREADCRUMB_PAGES: Page[] = [
  { name: 'Gestão de Conta Financeira', route: null, current: false },
  {
    name: 'Conta Financeira',
    route: DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE,
    current: false,
  },
  {
    name: 'Edição de Conta Financeira',
    route: null,
    current: true,
  },
];

const TITLE = 'Edição de Conta Financeira';

const UPDATE_ERROR_TOAST: ToastProps = {
  text: 'Houve um erro ao tentar atualizar a Conta Financeira',
  title: 'Algo deu errado!',
  variant: 'danger',
};
const UPDATE_SUCCESS_TOAST: ToastProps = {
  title: 'Successfully saved!',
  variant: 'primary',
  text: 'Anyone with a link can now view this file.',
};
const FETCH_ERROR_TOAST: ToastProps = {
  title: 'Algo deu errado!',
  variant: 'danger',
  text: 'Não foi possível carregar a Conta Financeira',
};

export default function EditFinancialAccountsPage() {
  const { ConfirmOverlay, showConfirm, closeConfirm } = useConfirm();
  const { id } = useParams<{ id: string }>();
  const { push } = useHistory();
  const { addToast } = useToastContext();
  const { t } = useTranslation();

  const { showLoading, closeLoading, LoadingOverlay } = useLoading();

  const [financialAccountUpdateMutation, { loading: updateLoading }] = useMutation<
    FinancialAccountUpdated,
    FinancialAccountUpdateMutationVariables
  >(FINANCIAL_ACCOUNT_UPDATE_MUTATION, {
    onError(error: ApolloError) {
      if (has(error.graphQLErrors[0], 'details')) {
        addToast(UPDATE_ERROR_TOAST);
      }

      setFormError(
        error,
        (field: string, error: ErrorOption) => {
          setError(field as keyof FormFields, error);

          setTimeout(() => clearErrors(), 2500);
        },
        t
      );
    },
    onCompleted() {
      addToast(UPDATE_SUCCESS_TOAST);
      push(DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE);
    },
  });

  const [financialAccountDeleteMutation, { loading: deleteLoading }] =
    useMutation<FinancialAccountDeleteMutationVariables>(
      FINANCIAL_ACCOUNT_DELETE_MUTATION,
      {
        onError() {
          closeConfirm();
          setConfirmOverlayProps(DEFAULT_OVERLAY_CONFIG);
          addToast(UPDATE_ERROR_TOAST);
        },
        onCompleted() {
          closeConfirm();
          setConfirmOverlayProps(DEFAULT_OVERLAY_CONFIG);
          addToast(UPDATE_SUCCESS_TOAST);
          push(DASHBOARD_FINANCIAL_ACCOUNTS_ROUTE);
        },
      }
    );

  const [
    financialAccountQuery,
    {
      data,
      loading: fetchFinancialAcccountLoading,
      error: fetchFinancialAccountError,
    },
  ] = useLazyQuery(FINANCIAL_ACCOUNT_QUERY, {
    variables: {
      id: id,
    },
  });

  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
    setValue,
    control,
  } = useForm<FormFields>();

  useEffect(() => {
    if (fetchFinancialAccountError) {
      addToast(FETCH_ERROR_TOAST);
    }
  }, [addToast, fetchFinancialAccountError]);

  useEffect(() => {
    if (data?.financialAccount) {
      FORM_FIELDS.forEach((field) => setValue(field, data.financialAccount[field]));
    }
  }, [data, setValue]);

  useEffect(() => {
    financialAccountQuery();
  }, [financialAccountQuery, id]);

  const [confirmOverlayProps, setConfirmOverlayProps] = useState<OverlayProps>(
    DEFAULT_OVERLAY_CONFIG
  );

  const isLoading = !!(
    deleteLoading ||
    updateLoading ||
    fetchFinancialAcccountLoading
  );

  useEffect(() => {
    if (isLoading) {
      showLoading();
      return;
    }

    closeLoading();
  }, [isLoading, showLoading, closeLoading]);

  const onSubmit: SubmitHandler<FormFields> = (financialAccountUpdateInput) => {
    financialAccountUpdateMutation({
      variables: {
        financialAccountUpdateInput: {
          id: Number(id),
          financialAccountLegalName:
            financialAccountUpdateInput.financialAccountLegalName,
          financialAccountDocumentId:
            financialAccountUpdateInput.financialAccountDocumentId,
          financialAccountAddressPostalCode:
            financialAccountUpdateInput.financialAccountAddressPostalCode,
          financialAccountAddressState:
            financialAccountUpdateInput.financialAccountAddressState,
          financialAccountAddressCity:
            financialAccountUpdateInput.financialAccountAddressCity,
          financialAccountAddressDistrict:
            financialAccountUpdateInput.financialAccountAddressDistrict,
          financialAccountAddressStreet:
            financialAccountUpdateInput.financialAccountAddressStreet,
          financialAccountAddressComplement:
            financialAccountUpdateInput.financialAccountAddressComplement,
        },
      },
    });
  };

  const onConfirmDelete = () =>
    financialAccountDeleteMutation({
      variables: { id: id },
      update(cache) {
        updateCacheById(cache, Number(id), financialAccountTypename);
      },
    });

  const onClickDelete = (overlayConfig: OverlayConfig) => {
    setConfirmOverlayProps({
      ...overlayConfig,
      onConfirm: onConfirmDelete,
    });

    showConfirm();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <LoadingOverlay />
      <Dashboard
        dashboardHeader={<div className="h-10" />}
        dashboardMainHeaderTitle={
          <DashboardMainHeaderForm title={TITLE} breadcrumbPages={BREADCRUMB_PAGES}>
            {data?.financialAccount && (
              <>
                {' '}
                <DeleteButton onClick={onClickDelete} disabled={isLoading} />
                <Button type="submit" size="sm" disabled={isLoading}>
                  Salvar
                </Button>
              </>
            )}
            <ConfirmOverlay {...confirmOverlayProps} onCancel={closeConfirm} />
          </DashboardMainHeaderForm>
        }
      >
        {data?.financialAccount && (
          <FinancialAccountFields
            errors={errors}
            register={register}
            control={control}
            financialAccountEntityTypeDisabled
          />
        )}
      </Dashboard>
    </form>
  );
}
